/**
 * <product-gallery>
 *
 * Handles PDP media gallery interactivity (thumbnail sync, video playback, 
 * image culling, accessibility announcements).
 * The track scrolling is handled natively by CSS scroll-snap.
 * Stays idle above 749px (static grid layout).
 */
const GALLERY_MOBILE_QUERY = '(max-width: 749px)';
// Multiple steps to identify the widest slide during scrolling.
const GALLERY_THRESHOLDS = [0.25, 0.5, 0.75];
// Keep decoded bitmaps within this distance to prevent memory drops on iOS.
const GALLERY_KEEP_DISTANCE = 2;
const GALLERY_ANNOUNCE_MS = 2000;
// Debounce time for screen reader announcements.
const GALLERY_SETTLE_MS = 250;

class ProductGallery extends HTMLElement {
  #track = null;
  #slides = [];
  #thumbTrack = null;
  #thumbs = [];
  #thumbForSlide = [];
  #liveRegion = null;
  #ratios = [];
  #activeIndex = -1;
  #controller = null;
  #observer = null;
  #mql = null;
  #announceTimer = null;
  #clearTimer = null;

  connectedCallback() {
    // Prevent double initialization.
    if (this.#controller) return;

    this.#track = this.querySelector('.js-gallery-track');
    if (!this.#track) return;

    this.#slides = Array.from(this.#track.children);
    this.#ratios = this.#slides.map(() => 0);
    this.#thumbTrack = this.querySelector('.js-gallery-thumbs');
    this.#thumbs = this.#thumbTrack ? Array.from(this.#thumbTrack.children) : [];
    this.#liveRegion = this.querySelector('.js-gallery-status');

    // Pair by media ID to ensure exact matching between slides and thumbnails.
    this.#thumbForSlide = this.#slides.map(
      (slide, index) =>
        this.#thumbs.find((thumb) => thumb.dataset.target === slide.dataset.mediaId) ?? this.#thumbs[index] ?? null,
    );

    // Use AbortController for clean listener removal.
    const { signal } = (this.#controller = new AbortController());

    this.#thumbs.forEach((thumb, index) => {
      const slideIndex = this.#thumbForSlide.indexOf(thumb);
      thumb.addEventListener('click', () => this.#goTo(slideIndex === -1 ? index : slideIndex), { signal });
    });

    this.#mql = window.matchMedia(GALLERY_MOBILE_QUERY);
    this.#mql.addEventListener('change', () => this.#syncToViewport(), { signal });
    this.#syncToViewport();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stopWatching();
    clearTimeout(this.#announceTimer);
    clearTimeout(this.#clearTimer);
    this.#pauseVideos();
  }

  #syncToViewport() {
    if (this.#mql.matches) {
      this.#startWatching();
      return;
    }

    this.#stopWatching();
    window.MediaCulling.restoreWithin(this.#track);
  }

  #startWatching() {
    if (this.#observer) return;

    // Ratios represent the slide's visibility within the scroll track.
    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      root: this.#track,
      threshold: GALLERY_THRESHOLDS,
    });
    this.#slides.forEach((slide) => this.#observer.observe(slide));
  }

  #stopWatching() {
    this.#observer?.disconnect();
    this.#observer = null;
    // Reset state when leaving mobile layout.
    this.#activeIndex = -1;
    this.#ratios = this.#ratios.map(() => 0);
  }

  #onIntersect(entries) {
    entries.forEach((entry) => {
      const index = this.#slides.indexOf(entry.target);
      if (index !== -1) this.#ratios[index] = entry.intersectionRatio;
    });

    let widest = 0;
    this.#ratios.forEach((ratio, index) => {
      if (ratio > this.#ratios[widest]) widest = index;
    });

    // Ignore updates if gallery is off-screen.
    if (!this.#ratios[widest]) return;

    this.#setActive(widest);
  }

  #setActive(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#syncThumbs(index);
    this.#syncVideos(index);
    this.#cullImages(index);
    this.#announce(index);
  }

  #syncThumbs(index) {
    if (!this.#thumbs.length) return;

    const active = this.#thumbForSlide[index] ?? null;

    this.#thumbs.forEach((thumb) => {
      const isActive = thumb === active;
      thumb.classList.toggle('active', isActive);

      const button = thumb.querySelector('button');
      if (!button) return;
      if (isActive) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    this.#scrollTo(this.#thumbTrack, active, true);
  }

  #goTo(index) {
    // Restore destination image before scrolling to prevent blank flashes.
    window.MediaCulling.restoreWithin(this.#slides[index]);
    this.#scrollTo(this.#track, this.#slides[index]);
  }

  /**
   * Scrolls `track` to bring `item` into view.
   * Motion is handled by CSS scroll-behavior.
   */
  #scrollTo(track, item, onlyWhenClipped = false) {
    if (!track || !item) return;

    const trackRect = track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    // Abort if no layout is established yet.
    if (!trackRect.width) return;

    const offset = itemRect.left - trackRect.left;
    const clipped = offset < 0 || itemRect.right > trackRect.right;
    if (onlyWhenClipped && !clipped) return;

    // Center items only when following scrolling, otherwise align flush.
    const centring = onlyWhenClipped ? (trackRect.width - itemRect.width) / 2 : 0;
    const target = track.scrollLeft + offset - centring;

    track.scrollTo({ left: Math.max(0, Math.min(target, track.scrollWidth - track.clientWidth)) });
  }

  #syncVideos(index) {
    this.#pauseVideos();

    // Play video if autoplay is enabled and loaded.
    const video = this.#slides[index]?.querySelector('video[autoplay]');
    video?.play().catch(() => {
      // Ignore autoplay rejections.
    });
  }

  #pauseVideos() {
    this.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause();
    });
  }

  #cullImages(activeIndex) {
    this.#slides.forEach((slide, index) => {
      const keep = Math.abs(index - activeIndex) <= GALLERY_KEEP_DISTANCE;
      if (keep) window.MediaCulling.restoreWithin(slide);
      else window.MediaCulling.cullWithin(slide);
    });
  }

  #announce(index) {
    const template = window.accessibilityStrings?.imageAvailable;
    if (!this.#liveRegion || !template) return;

    clearTimeout(this.#announceTimer);
    clearTimeout(this.#clearTimer);

    this.#announceTimer = setTimeout(() => {
      const position = this.#thumbForSlide[index]?.dataset.mediaPosition || index + 1;
      this.#liveRegion.textContent = template.replace('[index]', position);

      // Clear announcement to allow re-reading the same slide.
      this.#clearTimer = setTimeout(() => {
        this.#liveRegion.textContent = '';
      }, GALLERY_ANNOUNCE_MS);
    }, GALLERY_SETTLE_MS);
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
